import { tableCellClasses } from '@mui/material'
import type { Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

/**
 * Styles that pin a cell to the right edge of the horizontal scroll container,
 * so the actions column stays in view while wide tables scroll sideways.
 *
 * Returns a plain style object so callers can compose it into their own `sx`
 * (e.g. merge with `cursor: 'pointer'`) — the styled cell's `sx` accepts an
 * object or a theme function, but not an array.
 *
 * The cell must be opaque to occlude the scrolling content beneath it. Its
 * background comes from the `--cb-sticky-bg` CSS variable that
 * ScrollableTableContainer sets to the table's real surface color, so the
 * column always matches the rest of the rows (white, off-white, whatever it is)
 * instead of a hardcoded guess. The divider line is drawn as a pseudo-element
 * because the table resets cell borders (`.MuiTableCell-root { border: 0 }`),
 * which would swallow a real `border-left`. On row hover we paint a translucent
 * primary layer over the opaque base so the cell still reacts to hover.
 *
 * `width: 1%` + `nowrap` collapses the column to the minimum width that fits
 * its content (the action icons) instead of letting the table hand it extra
 * room.
 */
export const stickyEndCellStyles = (
  theme: Theme,
  variant: 'head' | 'body'
): SystemStyleObject<Theme> => ({
  position: 'sticky',
  right: 0,
  width: '1%',
  whiteSpace: 'nowrap',
  // As células da tabela têm só 4px de padding horizontal, o que basta numa
  // coluna larga mas cola os ícones no divisor quando a coluna encolhe para o
  // conteúdo (`width: 1%`). Repete o seletor de classe do styled da célula para
  // ganhar a especificidade e afastar o conteúdo das duas bordas.
  [`&.${tableCellClasses.head}, &.${tableCellClasses.body}`]: {
    paddingLeft: '12px',
    paddingRight: '12px'
  },
  // Header above body when scrolling sideways.
  zIndex: variant === 'head' ? 3 : 2,
  backgroundColor: 'var(--cb-sticky-bg, #fff)',
  // On phones the table is wider than the viewport, so a pinned column would
  // sit glued to the right edge permanently, occluding the row content beneath
  // it. Drop the sticky behavior (and its divider/shadow) below `sm` so the
  // actions column becomes a normal trailing column the user scrolls to reach.
  [theme.breakpoints.down('sm')]: {
    position: 'static',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  },
  ...(variant === 'body' && {
    // Divider line on the left edge — body rows only, never the header.
    // Pseudo-element survives the cell border reset (.MuiTableCell-root border 0).
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '1px',
      pointerEvents: 'none',
      backgroundColor: (theme.palette as any)?.neutral?.[700] ?? '#E3E6E3'
    },
    // Very faint depth shadow spilling to the left of the column (body only).
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '14px',
      transform: 'translateX(-100%)',
      pointerEvents: 'none',
      background:
        'linear-gradient(to left, rgba(16, 24, 40, 0.035), rgba(16, 24, 40, 0))'
    },
    'tr:hover &': {
      backgroundImage: `linear-gradient(${theme.palette.primary.main}10, ${theme.palette.primary.main}10)`
    }
  })
})

/** Convenience `sx` value for a sticky-end cell, optionally merged with base. */
export const stickyEndCellSx =
  (variant: 'head' | 'body', base?: SystemStyleObject<Theme>) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    ...base,
    ...stickyEndCellStyles(theme, variant)
  })
